import React from 'react';
import {
  useCreateDeliveryAddress,
  useUpdateDeliveryAddress,
} from 'hooks/DeliveryAddressHooks';
import { Button, Col, Content, ModalLayout, Row, Text } from 'components';
import { Input, Select, Spin } from 'antd';
import Formatter from 'utils/Formatter';
import {
  openAddressEmbed,
  openAddressModal,
  openAddressModalAsync,
} from 'utils/DaumAPIManager';
import MessageAlert, { isMobile } from 'utils';
import { useUserDataAction, useUserDataStack } from 'store';
import { useNavigate } from 'react-router-dom';

const styles = {
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
};

const DELIVERY_REQUEST = [
  {
    label: '문 앞에 놔주세요',
    value: '문 앞에 놔주세요',
  },
  {
    label: '경비실에 맡겨주세요',
    value: '경비실에 맡겨주세요',
  },
  {
    label: '택배함에 넣어주세요',
    value: '택배함에 넣어주세요',
  },
  {
    label: '배송 전에 연락 주세요',
    value: '배송 전에 연락 주세요',
  },
  {
    label: '직접입력',
    value: null,
  },
];

const AddressPopup = (props) => {
  /* ===== Props ===== */
  const { location, navigate } = props;
  const { route } = location.state || {};

  /* ===== Route ===== */
  const query = new URLSearchParams(location.search);
  const type = query.get('type') ?? 'create';
  const content = query.get('content');

  /* ===== Store ===== */
  const { deliveryAddress } = useUserDataStack();
  const userDataAction = useUserDataAction();

  /* ===== State ===== */
  const addressRef = React.useRef(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [inputValue, setInputValue] = React.useState({
    recipient_postcode: '',
    recipient_address: '',
    recipient_address_detail: '',
    recipient_name: '',
    recipient_phone: '',
    request: '',
  });
  const [selectedValue, setSelectedValue] = React.useState('');
  const [isOpened, setIsOpened] = React.useState(false);

  /* ===== Mutate ===== */
  const { createDeliveryAddress } = useCreateDeliveryAddress();
  const { updateDeliveryAddress } = useUpdateDeliveryAddress();

  /* ===== Function ===== */
  /**
   * 데이터 바인딩 함수
   * --
   */
  const handleChange = (t, v, callback = null) => {
    setInputValue((prevState) => {
      const newData = { ...prevState, [t]: v };

      if (callback) callback(v);

      return newData;
    });
  };

  /**
   * 다음 주소 API
   * --
   */
  const handleAddressModal = () => {
    const mobileCheck = isMobile();

    if (!mobileCheck) {
      openAddressModal((res) => {
        const { address, zonecode } = res;

        handleChange('recipient_address', address);
        handleChange('recipient_postcode', zonecode);
      });
    } else {
      setIsOpened(!isOpened);
    }
  };

  /**
   * 배송지 생성/수정
   * --
   * @returns
   */
  const handleSubmit = async () => {
    const {
      recipient_postcode,
      recipient_address,
      recipient_name,
      recipient_phone,
      recipient_address_detail,
    } = inputValue;

    if (!recipient_name) {
      MessageAlert.warning('받으시는 분의 성함을 입력해주세요!');
      return;
    }

    if (!recipient_phone) {
      MessageAlert.warning('받으시는 분의 연락처를 입력해주세요!');
      return;
    }

    if (recipient_phone.length < 12) {
      MessageAlert.warning('잘못된 연락처 형식입니다!');
      return;
    }

    if (!recipient_postcode || !recipient_address) {
      MessageAlert.warning('받으시는 분의 주소를 입력해주세요!');
      return;
    }

    if (!recipient_address_detail) {
      MessageAlert.warning('받으시는 분의 상세 주소를 입력해주세요!');
      return;
    }

    const submitType = type.toLocaleUpperCase();

    try {
      setIsLoading(true);

      switch (submitType) {
        case 'CREATE':
          const { status: createStatus, data: createData } =
            await createDeliveryAddress({
              ...inputValue,
              ...JSON.parse(content),
            });

          if (createStatus !== 200) {
            MessageAlert.warning('배송지를 추가하는데 실패하였습니다!');
            return;
          }

          userDataAction.addData('deliveryAddress', createData);

          window.alert('배송지가 추가되었습니다');

          if (!isMobile()) {
            const createDelivery = {
              data: JSON.stringify(createData),
              source: 'popup-create',
            };

            window.opener.postMessage(createDelivery, '*');
            setTimeout(() => {
              window.close();
            }, 300);
          } else {
            navigate(route ?? '/');
          }

          return;
        case 'UPDATE':
          const { status: updateStatus, data: updateData } =
            await updateDeliveryAddress({
              ...inputValue,
            });

          if (updateStatus !== 200) {
            MessageAlert.warning('배송지를 수정하는데 실패하였습니다!');
            return;
          }

          const fid = deliveryAddress.findIndex(
            (item) =>
              item?.delivery_address_id === updateData?.delivery_address_id
          );

          userDataAction.fetchData('deliveryAddress', fid, updateData);

          window.alert('배송지가 수정되었습니다');

          if (!isMobile()) {
            const updateDelivery = {
              data: JSON.stringify(updateData),
              source: 'popup-update',
            };

            window.opener.postMessage(updateDelivery, '*');
            setTimeout(() => {
              window.close();
            }, 300);
          } else {
            navigate(route ?? '/');
          }

          return;
        default:
          return MessageAlert.info('배송지 생성/수정에 실패하였습니다!');
      }
    } catch (err) {
      console.error('[handleSubmit] 배송지 Error!! : ', err);
      MessageAlert.error('배송지 생성/수정에 실패하였습니다!');
      return;
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  /* ===== Hooks ===== */
  React.useEffect(() => {
    setInputValue((prev) => ({
      ...prev,
      ...JSON.parse(content),
    }));
  }, []);

  React.useEffect(() => {
    if (isOpened) {
      openAddressEmbed(addressRef.current, (res) => {
        const { address, zonecode } = res;

        handleChange('recipient_address', address);
        handleChange('recipient_postcode', zonecode);
        setIsOpened(false);
      });
    }
  }, [isOpened]);

  /* ===== Render ===== */
  return (
    <React.Fragment>
      <Spin spinning={isLoading}>
        <Content
          maxWidth={'100%'}
          style={{ height: 700, position: 'relative', top: 0, left: 0 }}
        >
          <Row>
            <Col x={24}>
              <Content maxWidth={'100%'} padding={0}>
                <Text size={18} weight={700}>
                  배송지 {type === 'create' ? '추가' : '수정'}
                </Text>
              </Content>
            </Col>
          </Row>

          <Row>
            <Col x={24}>
              <Content
                maxWidth={'100%'}
                padding={0}
                style={{
                  marginTop: 30,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 20,
                }}
              >
                <div style={{ ...styles.form }}>
                  <Text size={14}>이름</Text>
                  <Input
                    size="large"
                    placeholder="받는 분의 성함을 입력해주세요"
                    value={inputValue.recipient_name}
                    onChange={(e) =>
                      handleChange('recipient_name', e.target.value)
                    }
                  />
                </div>

                <div style={{ ...styles.form }}>
                  <Text size={14}>휴대폰번호</Text>
                  <Input
                    size="large"
                    placeholder="받는 분의 휴대폰번호를 입력해주세요"
                    value={inputValue.recipient_phone}
                    onChange={(e) =>
                      handleChange(
                        'recipient_phone',
                        Formatter.hyphen(e.target.value)
                      )
                    }
                  />
                </div>

                <div style={{ ...styles.form }}>
                  <Text size={14}>주소</Text>
                  <div
                    style={{ display: 'flex', alignItems: 'stretch', gap: 10 }}
                  >
                    <Input
                      size="large"
                      placeholder="우편번호"
                      value={inputValue.recipient_postcode}
                      onChange={(e) =>
                        handleChange(
                          'recipient_postcode',
                          Formatter.hyphen(e.target.value)
                        )
                      }
                      readOnly
                    />
                    <button
                      style={{
                        border: '1px solid #111111',
                        background: '#FFFFFF',
                        cursor: 'pointer',
                        fontSize: 14,
                        padding: '2px 10px',
                        whiteSpace: 'nowrap',
                      }}
                      onClick={() => handleAddressModal()}
                    >
                      주소 찾기
                    </button>
                  </div>
                  <Input
                    size="large"
                    placeholder="주소"
                    value={inputValue.recipient_address}
                    onChange={(e) =>
                      handleChange('recipient_address', e.target.value)
                    }
                    readOnly
                  />
                  <Input
                    size="large"
                    placeholder="주소 상세"
                    value={inputValue.recipient_address_detail}
                    onChange={(e) =>
                      handleChange('recipient_address_detail', e.target.value)
                    }
                  />
                </div>

                <div style={{ ...styles.form }}>
                  <Text size={14}>배송 요청사항 (선택)</Text>
                  <Select
                    size="large"
                    options={DELIVERY_REQUEST}
                    placeholder="배송 요청사항을 선택해주세요"
                    value={selectedValue}
                    onChange={(e) => {
                      handleChange('request', e);
                      setSelectedValue(e);
                    }}
                  />
                  {selectedValue === null ? (
                    <Input.TextArea
                      style={{ height: 60 }}
                      maxLength={50}
                      value={inputValue.request}
                      onChange={(e) => handleChange('request', e.target.value)}
                      showCount
                    />
                  ) : null}
                </div>
              </Content>
            </Col>
          </Row>

          <Button
            style={{
              width: 'calc(100% - 30px)',
              borderRadius: 0,
              position: 'absolute',
              bottom: 20,
            }}
            onClick={() => handleSubmit()}
          >
            {type === 'create' ? '저장하기' : '수정하기'}
          </Button>
        </Content>
      </Spin>

      <ModalLayout
        open={isOpened}
        type="drawer"
        placement="bottom"
        width={'100%'}
        height="100%"
        closable
        onCancel={() => setIsOpened(false)}
        styles={{
          body: {
            padding: 0,
          },
        }}
      >
        <div ref={addressRef}></div>
      </ModalLayout>
    </React.Fragment>
  );
};

export default AddressPopup;
